import React, { useState, useEffect, useRef } from 'react';
import {
  Layout,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
  Popconfirm,
  message,
  Tag,
  Switch,
  Row,
  Col,
  Spin,
  Empty,
  Tooltip,
  Alert,
  Progress,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SafetyCertificateOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  FileExcelOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import API from '../config/api';
import DashboardSidebar from './DashboardSidebar';
import DashboardHeader from './DashboardHeader';

const { Content } = Layout;
const { Title, Text } = Typography;

interface InsuranceCompany {
  _id: string;
  company_id: string;
  doctor_id: string;
  name_ar: string;
  name_en: string;
  code?: string;
  contract_number?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  coverage_percentage: number;
  notes: string;
  isActive: boolean;
  createdAt: string;
}

interface InsurancePriceListSummary {
  priceListId: string;
  year: number;
  effectiveDate?: string;
  expiryDate?: string | null;
  name: string;
  is_active: boolean;
  itemsCount: number;
}

interface InsurancePriceItem {
  itemType: 'visit_type' | 'service' | 'custom';
  itemId: string;
  visitTypeId: string;
  visitTypeName?: string;
  visitTypeNameEn?: string;
  serviceId?: string;
  serviceName?: string;
  category?: string;
  normalPrice?: number;
  urgentPrice?: number | null;
  insurancePrice?: number | null;
  isVisitTypeActive?: boolean;
  isServiceActive?: boolean;
}

interface InsurancePriceListDetail {
  priceListId: string;
  year: number;
  effectiveDate?: string;
  expiryDate?: string | null;
  name: string;
  visitTypes: InsurancePriceItem[];
  services?: InsurancePriceItem[];
  items?: InsurancePriceItem[];
}

interface ImportRow {
  name_ar: string;
  name_en: string;
  coverage_percentage: number;
  notes: string;
  status?: 'pending' | 'success' | 'error';
  errorMsg?: string;
}

const MAX_IMPORT_ROWS = 200;
const MAX_FILE_MB = 5;

// Normalize Arabic text: unify alef variants, ya/alef-maqsura, remove diacritics & invisible chars
const normalizeAr = (s: string): string =>
  s
    .replace(/[\u0623\u0625\u0622\u0671]/g, '\u0627') // أ إ آ ٱ  → ا
    .replace(/\u0649/g, '\u064A')                      // ى → ي
    .replace(/[\u064B-\u065F\u0670]/g, '')             // strip tashkeel / diacritics
    .replace(/[\u200B-\u200F\u202A-\u202E\uFEFF]/g, '') // strip invisible/directional chars
    .replace(/\s+/g, '')                               // strip all whitespace
    .toLowerCase();

// Fuzzy column header matching (Arabic-aware)
const matchColumn = (headers: string[], ...keywords: string[]): string | null => {
  const normHeaders = headers.map(normalizeAr);
  for (const kw of keywords) {
    const normKw = normalizeAr(kw);
    const idx = normHeaders.findIndex(h => h.includes(normKw));
    if (idx !== -1) return headers[idx];
  }
  return null;
};

const InsuranceCompaniesManagement: React.FC = () => {
  const [companies, setCompanies] = useState<InsuranceCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCompany, setEditingCompany] = useState<InsuranceCompany | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // Excel import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [importDone, setImportDone] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const [priceModalVisible, setPriceModalVisible] = useState(false);
  const [priceCompany, setPriceCompany] = useState<InsuranceCompany | null>(null);
  const [priceLists, setPriceLists] = useState<InsurancePriceListSummary[]>([]);
  const [priceYear, setPriceYear] = useState<number>(new Date().getFullYear());
  const [priceDate, setPriceDate] = useState<string>(`${new Date().getFullYear()}-01-01`);
  const [priceExpiryDate, setPriceExpiryDate] = useState<string>('');
  const [priceListData, setPriceListData] = useState<InsurancePriceListDetail | null>(null);
  const [priceValues, setPriceValues] = useState<Record<string, number | null>>({});
  const [priceLoading, setPriceLoading] = useState(false);
  const [priceSaving, setPriceSaving] = useState(false);
  const [priceCreating, setPriceCreating] = useState(false);
  const [priceDeleting, setPriceDeleting] = useState<string | null>(null);

  const doctorId = localStorage.getItem('doctorId');
  const clinicId = localStorage.getItem('clinicId') || '';
  const baseUrl = API.BASE_URL;

  const fetchCompanies = async () => {
    if (!doctorId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${baseUrl}${API.ENDPOINTS.INSURANCE_COMPANIES(doctorId)}?includeInactive=true`
      );
      setCompanies(res.data?.data || []);
    } catch (err) {
      message.error('فشل تحميل شركات التأمين');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, [doctorId]);

  const openAddModal = () => {
    setEditingCompany(null);
    form.resetFields();
    setModalVisible(true);
  };

  const openEditModal = (company: InsuranceCompany) => {
    setEditingCompany(company);
    form.setFieldsValue({
      name_ar: company.name_ar,
      name_en: company.name_en,
      code: company.code,
      contract_number: company.contract_number,
      contract_start_date: company.contract_start_date,
      contract_end_date: company.contract_end_date,
      notes: company.notes
    });
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingCompany) {
        await axios.put(
          `${baseUrl}${API.ENDPOINTS.INSURANCE_COMPANY(doctorId!, editingCompany.company_id)}`,
          values
        );
        message.success('تم تحديث شركة التأمين');
      } else {
        await axios.post(`${baseUrl}${API.ENDPOINTS.INSURANCE_COMPANIES_CREATE}`, {
          ...values,
          coverage_percentage: 0,
          doctor_id: doctorId
        });
        message.success('تم إضافة شركة التأمين');
      }
      setModalVisible(false);
      fetchCompanies();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error('حدث خطأ أثناء الحفظ');
    }
  };

  const getPriceListUrl = (company: InsuranceCompany, period?: number | string) => {
    if (!clinicId) return '';
    if (period) return `${baseUrl}${API.ENDPOINTS.INSURANCE_PRICE_LIST(clinicId, company.company_id, period)}`;
    return `${baseUrl}${API.ENDPOINTS.INSURANCE_PRICE_LISTS(clinicId, company.company_id)}`;
  };

  const loadPriceLists = async (company: InsuranceCompany) => {
    if (!clinicId) {
      message.error('Clinic ID is missing');
      return [];
    }
    const res = await axios.get(getPriceListUrl(company));
    const lists = res.data?.data || [];
    setPriceLists(lists);
    return lists;
  };

  const loadPriceList = async (company: InsuranceCompany, period: number | string) => {
    if (!doctorId || !clinicId) return;
    setPriceLoading(true);
    try {
      const res = await axios.get(`${getPriceListUrl(company, period)}?doctor_id=${doctorId}`);
      const data = res.data?.data || null;
      setPriceListData(data);
      setPriceExpiryDate(data?.expiryDate || '');
      const values: Record<string, number | null> = {};
      (data?.items || data?.visitTypes || []).forEach((item: InsurancePriceItem) => {
        values[`${item.itemType || 'visit_type'}:${item.itemId || item.visitTypeId}`] = item.insurancePrice ?? null;
      });
      setPriceValues(values);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setPriceListData(null);
        setPriceValues({});
        return;
      }
      message.error('Failed to load price list');
    } finally {
      setPriceLoading(false);
    }
  };

  const openPriceListModal = async (company: InsuranceCompany) => {
    const year = new Date().getFullYear();
    setPriceCompany(company);
    setPriceYear(year);
    setPriceDate(`${year}-01-01`);
    setPriceExpiryDate('');
    setPriceLists([]);
    setPriceListData(null);
    setPriceValues({});
    setPriceModalVisible(true);
    try {
      await loadPriceLists(company);
      await loadPriceList(company, `${year}-01-01`);
    } catch {
      message.error('Failed to load price lists');
    }
  };

  const createPriceList = async () => {
    if (!priceCompany || !doctorId || !clinicId || !priceDate) return;
    if (priceExpiryDate && priceExpiryDate < priceDate) {
      message.error('Expiry date cannot be before the effective date');
      return;
    }
    setPriceCreating(true);
    try {
      await axios.post(getPriceListUrl(priceCompany), {
        year: Number(priceDate.slice(0, 4)),
        effective_date: priceDate,
        ...(priceExpiryDate ? { expiry_date: priceExpiryDate } : {}),
        created_by_owner_id: doctorId,
      });
      message.success('Price list created');
      await loadPriceLists(priceCompany);
      await loadPriceList(priceCompany, priceDate);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to create price list');
    } finally {
      setPriceCreating(false);
    }
  };

  const savePriceListItems = async () => {
    if (!priceCompany || !clinicId || !priceDate || !priceListData) return;
    setPriceSaving(true);
    try {
      const savePeriod = priceListData.effectiveDate || priceDate;
      const rows = priceListData.items || priceListData.visitTypes || [];
      const prices = rows.map((item) => ({
        itemType: item.itemType || 'visit_type',
        itemId: item.itemId || item.visitTypeId,
        label: item.itemType === 'custom' ? (item.visitTypeName || '') : '',
        price: priceValues[`${item.itemType || 'visit_type'}:${item.itemId || item.visitTypeId}`] ?? null,
      }));
      await axios.put(
        `${baseUrl}${API.ENDPOINTS.INSURANCE_PRICE_LIST_ITEMS(clinicId, priceCompany.company_id, savePeriod)}`,
        { prices }
      );
      message.success('Price list saved');
      await loadPriceList(priceCompany, savePeriod);
      await loadPriceLists(priceCompany);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to save price list');
    } finally {
      setPriceSaving(false);
    }
  };

  const deletePriceList = async (list: InsurancePriceListSummary) => {
    if (!priceCompany || !clinicId) return;
    const period = list.effectiveDate || list.year;
    setPriceDeleting(String(period));
    try {
      await axios.delete(`${baseUrl}${API.ENDPOINTS.INSURANCE_PRICE_LIST(clinicId, priceCompany.company_id, period)}`);
      message.success('Price list deleted');
      const remaining = await loadPriceLists(priceCompany);
      const next = remaining[0];
      if (next) {
        const nextPeriod = next.effectiveDate || `${next.year}-01-01`;
        setPriceYear(next.year);
        setPriceDate(nextPeriod);
        await loadPriceList(priceCompany, nextPeriod);
      } else {
        setPriceListData(null);
        setPriceValues({});
      }
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to delete price list');
    } finally {
      setPriceDeleting(null);
    }
  };

  const addCustomPriceItem = () => {
    if (!priceListData) return;
    const label = window.prompt('Custom insurance item name');
    if (!label?.trim()) return;
    const itemId = `custom_${Date.now()}`;
    const item: InsurancePriceItem = { itemType: 'custom', itemId, visitTypeId: itemId, visitTypeName: label.trim(), insurancePrice: null };
    setPriceListData((prev) => prev ? { ...prev, items: [...(prev.items || prev.visitTypes || []), item] } : prev);
    setPriceValues((prev) => ({ ...prev, [`custom:${itemId}`]: null }));
  };

  const handleToggleActive = async (company: InsuranceCompany) => {
    try {
      await axios.put(
        `${baseUrl}${API.ENDPOINTS.INSURANCE_COMPANY(doctorId!, company.company_id)}`,
        { isActive: !company.isActive }
      );
      message.success(company.isActive ? 'تم تعطيل الشركة' : 'تم تفعيل الشركة');
      fetchCompanies();
    } catch {
      message.error('فشل تغيير الحالة');
    }
  };

  const handleDelete = async (company: InsuranceCompany) => {
    try {
      await axios.delete(
        `${baseUrl}${API.ENDPOINTS.INSURANCE_COMPANY(doctorId!, company.company_id)}?permanent=true`
      );
      message.success('تم حذف الشركة');
      fetchCompanies();
    } catch {
      message.error('فشل الحذف');
    }
  };

  // ── Excel import helpers ────────────────────────────────────────
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset file input so the same file can be re-selected
    e.target.value = '';

    // Validate file type
    const allowed = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!allowed.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      setParseError('يُسمح فقط بملفات Excel (.xlsx أو .xls)');
      setImportModalVisible(true);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      setParseError(`حجم الملف يتجاوز ${MAX_FILE_MB} MB المسموح به.`);
      setImportModalVisible(true);
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target?.result;
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        if (raw.length < 2) {
          setParseError('الملف فارغ أو لا يحتوي على بيانات.');
          setImportModalVisible(true);
          return;
        }

        // Extract header row (row 0), sanitize
        const headers: string[] = raw[0].map((h: any) =>
          String(h ?? '').trim()
        );

        // Fuzzy-match columns
        const colAr = matchColumn(headers, 'عربي', 'arabic', 'ar', 'اسم بالعربي', 'الاسم بالعربي', 'name_ar');
        const colEn = matchColumn(headers,
          'إنجليزي', 'انجليزي', 'الاسم بالإنجليزي', 'الاسم بالانجليزي',
          'اسم بالإنجليزي', 'اسم بالانجليزي', 'اسم انجليزي', 'اسم إنجليزي',
          'english', 'name_en', 'en');
        const colNotes = matchColumn(headers, 'ملاحظات', 'notes', 'note', 'ملاحظة');

        if (!colAr) {
          setParseError(
            'لم يتم العثور على عمود اسم الشركة بالعربي.'
          );
          setImportModalVisible(true);
          return;
        }

        const idxAr = colAr ? headers.indexOf(colAr) : -1;
        const idxEn = colEn ? headers.indexOf(colEn) : -1;
        const idxNotes = colNotes ? headers.indexOf(colNotes) : -1;

        const rows: ImportRow[] = [];

        for (let i = 1; i < raw.length && rows.length < MAX_IMPORT_ROWS; i++) {
          const row = raw[i];
          const nameAr = String(row[idxAr] ?? '').trim().slice(0, 200);
          const nameEn = String(row[idxEn] ?? '').trim().slice(0, 200);
          const notes = String(row[idxNotes] ?? '').trim().slice(0, 500);

          // Skip completely empty rows
          if (!nameAr && !nameEn && !notes) continue;

          const entry: ImportRow = {
            name_ar: nameAr,
            name_en: nameEn,
            coverage_percentage: 0,
            notes,
            status: 'pending',
          };

          if (!entry.name_ar) {
            entry.status = 'error';
            entry.errorMsg = 'الاسم بالعربي مطلوب';
          }

          rows.push(entry);
        }

        if (rows.length === 0) {
          setParseError('لا توجد بيانات صالحة في الملف.');
          setImportModalVisible(true);
          return;
        }

        setParseError(null);
        setImportRows(rows);
        setImportProgress(0);
        setImportDone(false);
        setImportModalVisible(true);
      } catch {
        setParseError('فشل قراءة الملف. تأكد أنه ملف Excel صالح.');
        setImportModalVisible(true);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    const validRows = importRows.filter(r => r.status !== 'error');
    if (validRows.length === 0) {
      message.warning('لا توجد بيانات صالحة للاستيراد.');
      return;
    }

    setImporting(true);
    setImportProgress(0);
    const updated = [...importRows];

    for (let i = 0; i < updated.length; i++) {
      if (updated[i].status === 'error') continue;

      try {
        await axios.post(`${baseUrl}${API.ENDPOINTS.INSURANCE_COMPANIES_CREATE}`, {
          name_ar: updated[i].name_ar,
          name_en: updated[i].name_en || '',
          coverage_percentage: 0,
          notes: updated[i].notes || '',
          doctor_id: doctorId,
        });
        updated[i] = { ...updated[i], status: 'success' };
      } catch (err: any) {
        updated[i] = {
          ...updated[i],
          status: 'error',
          errorMsg: err?.response?.data?.message || 'فشل الإضافة',
        };
      }

      setImportRows([...updated]);
      setImportProgress(Math.round(((i + 1) / updated.length) * 100));
    }

    setImporting(false);
    setImportDone(true);
    fetchCompanies();

    const successCount = updated.filter(r => r.status === 'success').length;
    const errorCount = updated.filter(r => r.status === 'error').length;
    if (errorCount === 0) {
      message.success(`تم استيراد ${successCount} شركة بنجاح.`);
    } else {
      message.warning(`تم استيراد ${successCount} شركة، فشل ${errorCount}.`);
    }
  };

  const closeImportModal = () => {
    if (importing) return;
    setImportModalVisible(false);
    setImportRows([]);
    setParseError(null);
    setImportDone(false);
    setImportProgress(0);
  };

  const importColumns = [
    {
      title: 'الاسم بالعربي',
      dataIndex: 'name_ar',
      key: 'name_ar',
      render: (t: string) => <strong>{t || <Text type="secondary">—</Text>}</strong>,
    },
    {
      title: 'الاسم بالإنجليزي',
      dataIndex: 'name_en',
      key: 'name_en',
      render: (t: string) => t || <Text type="secondary">—</Text>,
    },
    {
      title: 'الحالة',
      key: 'status',
      render: (_: any, row: ImportRow) => {
        if (row.status === 'success') return <Tag icon={<CheckCircleOutlined />} color="success">نجح</Tag>;
        if (row.status === 'error') return <Tag icon={<CloseCircleOutlined />} color="error">{row.errorMsg || 'خطأ'}</Tag>;
        return <Tag color="default">في الانتظار</Tag>;
      },
    },
  ];

  // ── Table columns ────────────────────────────────────────────────
  const columns = [
    {
      title: 'الاسم بالعربي',
      dataIndex: 'name_ar',
      key: 'name_ar',
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: 'الاسم بالإنجليزي',
      dataIndex: 'name_en',
      key: 'name_en',
      render: (text: string) => text || <Text type="secondary">—</Text>
    },
    {
      title: 'Contract',
      key: 'contract',
      render: (_: any, record: InsuranceCompany) => (
        <Space direction="vertical" size={0}>
          <Text>{record.code || record.contract_number || <Text type="secondary">-</Text>}</Text>
          {(record.contract_start_date || record.contract_end_date) && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.contract_start_date || '...'} / {record.contract_end_date || '...'}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'ملاحظات',
      dataIndex: 'notes',
      key: 'notes',
      render: (text: string) => text || <Text type="secondary">—</Text>
    },
    {
      title: 'الحالة',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (active: boolean, record: InsuranceCompany) => (
        <Tooltip title={active ? 'إيقاف الشركة' : 'تفعيل الشركة'}>
          <Switch
            checked={active}
            onChange={() => handleToggleActive(record)}
            checkedChildren="نشط"
            unCheckedChildren="متوقف"
          />
        </Tooltip>
      )
    },
    {
      title: 'إجراءات',
      key: 'actions',
      render: (_: any, record: InsuranceCompany) => (
        <Space>
          <Button
            icon={<SafetyCertificateOutlined />}
            size="small"
            onClick={() => openPriceListModal(record)}
          >
            Price Lists
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEditModal(record)}
          >
            تعديل
          </Button>
          <Popconfirm
            title="هل أنت متأكد من الحذف النهائي؟"
            onConfirm={() => handleDelete(record)}
            okText="حذف"
            cancelText="إلغاء"
            okButtonProps={{ danger: true }}
          >
            <Button icon={<DeleteOutlined />} size="small" danger>
              حذف
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh', direction: 'rtl' }}>
      <DashboardSidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      <Layout>
        <DashboardHeader onSettingsClick={() => navigate('/settings')} onMenuClick={() => {}} />
        <Content style={{ padding: '24px', background: '#f5f5f5' }}>
          <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
            <Col>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/dashboard')}
                style={{ marginLeft: 12 }}
              >
                رجوع
              </Button>
              <Title level={3} style={{ display: 'inline', margin: 0 }}>
                <SafetyCertificateOutlined style={{ marginLeft: 8 }} />
                إدارة شركات التأمين
              </Title>
            </Col>
            <Col>
              <Space>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={() => fileInputRef.current?.click()}
                  style={{ borderColor: '#52c41a', color: '#52c41a' }}
                >
                  استيراد من Excel
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
                  إضافة شركة تأمين
                </Button>
              </Space>
            </Col>
          </Row>

          <Card>
            {loading ? (
              <div style={{ textAlign: 'center', padding: 48 }}>
                <Spin size="large" />
              </div>
            ) : companies.length === 0 ? (
              <Empty description="لا توجد شركات تأمين مضافة حتى الآن. أضف أول شركة." />
            ) : (
              <Table
                dataSource={companies}
                columns={columns}
                rowKey="company_id"
                pagination={false}
                bordered
              />
            )}
          </Card>

          {/* ─── Excel Import Modal ─────────────────────────────── */}
          <Modal
            title={
              <Space>
                <FileExcelOutlined style={{ color: '#52c41a' }} />
                استيراد شركات التأمين من Excel
              </Space>
            }
            open={importModalVisible}
            onCancel={closeImportModal}
            footer={
              parseError ? (
                <Button onClick={closeImportModal}>إغلاق</Button>
              ) : importDone ? (
                <Button type="primary" onClick={closeImportModal}>إغلاق</Button>
              ) : (
                <Space>
                  <Button onClick={closeImportModal} disabled={importing}>إلغاء</Button>
                  <Button
                    type="primary"
                    icon={<UploadOutlined />}
                    onClick={handleImport}
                    loading={importing}
                    disabled={importRows.filter(r => r.status !== 'error').length === 0}
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  >
                    استيراد ({importRows.filter(r => r.status !== 'error').length} شركة)
                  </Button>
                </Space>
              )
            }
            width={720}
            destroyOnClose
            closable={!importing}
            maskClosable={!importing}
          >
            {parseError ? (
              <Alert
                type="error"
                icon={<WarningOutlined />}
                showIcon
                message="خطأ في قراءة الملف"
                description={parseError}
              />
            ) : (
              <div>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={`تم اكتشاف ${importRows.length} صف. الصفوف الصالحة: ${importRows.filter(r => r.status !== 'error').length}`}
                  description={
                    importRows.some(r => r.status === 'error') ? (
                      <span>
                        <WarningOutlined style={{ color: '#faad14' }} />
                        {' '}الصفوف المحددة بـ "خطأ" لن يتم استيرادها.
                      </span>
                    ) : undefined
                  }
                />
                {importing && (
                  <Progress percent={importProgress} style={{ marginBottom: 12 }} />
                )}
                <Table
                  dataSource={importRows}
                  columns={importColumns}
                  rowKey={(_, idx) => String(idx)}
                  pagination={{ pageSize: 10, hideOnSinglePage: true }}
                  size="small"
                  bordered
                  rowClassName={row =>
                    row.status === 'error'
                      ? 'import-row-error'
                      : row.status === 'success'
                      ? 'import-row-success'
                      : ''
                  }
                />
                <Divider style={{ margin: '8px 0' }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  الحد الأقصى للاستيراد: {MAX_IMPORT_ROWS} شركة لكل ملف — حجم الملف لا يتجاوز {MAX_FILE_MB} MB
                </Text>
              </div>
            )}
          </Modal>

          <Modal
            title={editingCompany ? 'تعديل شركة التأمين' : 'إضافة شركة تأمين جديدة'}
            open={modalVisible}
            onOk={handleSave}
            onCancel={() => setModalVisible(false)}
            okText={editingCompany ? 'حفظ التعديلات' : 'إضافة'}
            cancelText="إلغاء"
            width={500}
          >
            <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
              <Form.Item
                name="name_ar"
                label="اسم الشركة بالعربي"
                rules={[{ required: true, message: 'يرجى إدخال اسم الشركة بالعربي' }]}
              >
                <Input placeholder="مثال: التأمين الأهلي" />
              </Form.Item>
              <Form.Item name="name_en" label="اسم الشركة بالإنجليزي (اختياري)">
                <Input placeholder="e.g. National Insurance" />
              </Form.Item>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="code" label="Company Code">
                    <Input placeholder="Optional code" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="contract_number" label="Contract Number">
                    <Input placeholder="Optional contract number" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="contract_start_date" label="Contract Start">
                    <Input type="date" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="contract_end_date" label="Contract End">
                    <Input type="date" />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="notes" label="ملاحظات (اختياري)">
                <Input.TextArea rows={3} placeholder="أي تفاصيل إضافية..." />
              </Form.Item>
            </Form>
          </Modal>
          <Modal
            title={priceCompany ? `Insurance Price Lists - ${priceCompany.name_ar}` : 'Insurance Price Lists'}
            open={priceModalVisible}
            onCancel={() => setPriceModalVisible(false)}
            footer={[
              <Button key="close" onClick={() => setPriceModalVisible(false)}>Close</Button>,
              <Button key="save" type="primary" loading={priceSaving} disabled={!priceListData} onClick={savePriceListItems}>
                Save Prices
              </Button>
            ]}
            width={900}
            destroyOnClose
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Row gutter={12} align="bottom">
                <Col xs={24} md={8}>
                  <Text strong>Effective date</Text>
                  <Input
                    type="date"
                    value={priceDate}
                    onChange={(e) => {
                      const nextDate = e.target.value;
                      setPriceDate(nextDate);
                      setPriceYear(Number(nextDate.slice(0, 4)));
                      if (priceExpiryDate && priceExpiryDate < nextDate) setPriceExpiryDate('');
                    }}
                    style={{ width: '100%', marginTop: 6 }}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <Text strong>Expiry date <Text type="secondary">(optional)</Text></Text>
                  <Input
                    type="date"
                    value={priceExpiryDate}
                    min={priceDate || undefined}
                    onChange={(e) => setPriceExpiryDate(e.target.value)}
                    status={priceExpiryDate && priceExpiryDate < priceDate ? 'error' : undefined}
                    style={{ width: '100%', marginTop: 6 }}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <Space wrap>
                    <Button type="primary" onClick={createPriceList} loading={priceCreating}>
                      Create Price List
                    </Button>
                  </Space>
                </Col>
              </Row>

              {priceLists.length > 0 && (
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>Existing price-list dates</Text>
                  <Space wrap>
                    {priceLists.map((list) => (
                      <Space.Compact key={list.priceListId}>
                        <Button
                          type={list.effectiveDate === priceDate || (!list.effectiveDate && list.year === priceYear) ? 'primary' : 'default'}
                          size="small"
                          onClick={() => {
                            setPriceYear(list.year);
                            setPriceDate(list.effectiveDate || `${list.year}-01-01`);
                            setPriceExpiryDate(list.expiryDate || '');
                            if (priceCompany) loadPriceList(priceCompany, list.effectiveDate || list.year);
                          }}
                        >
                          {(list.effectiveDate || list.year)}{list.expiryDate ? ` → ${list.expiryDate}` : ''} ({list.itemsCount})
                        </Button>
                        <Popconfirm
                          title="Delete this price list?"
                          description="This deletes only this insurance price list and its prices."
                          okText="Delete"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => deletePriceList(list)}
                        >
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            loading={priceDeleting === String(list.effectiveDate || list.year)}
                          />
                        </Popconfirm>
                      </Space.Compact>
                    ))}
                  </Space>
                </div>
              )}

              {priceLoading ? (
                <div style={{ textAlign: 'center', padding: 32 }}>
                  <Spin />
                </div>
              ) : priceListData ? (
                <>
                <Button onClick={addCustomPriceItem} style={{ marginBottom: 8 }}>+ Add custom item</Button>
                <Table
                  dataSource={priceListData.items || priceListData.visitTypes}
                  rowKey={(item: InsurancePriceItem) => `${item.itemType || 'visit_type'}:${item.itemId || item.visitTypeId}`}
                  pagination={false}
                  size="small"
                  bordered
                  columns={[
                    {
                      title: 'Item',
                      key: 'itemName',
                      render: (_: any, item: InsurancePriceItem) => (
                        <Space direction="vertical" size={0}>
                          <Space>
                            <Tag color={item.itemType === 'service' ? 'green' : 'blue'}>
                              {item.itemType === 'service' ? 'Service' : item.itemType === 'custom' ? 'Custom' : 'Visit'}
                            </Tag>
                            <Text strong>{item.itemType === 'service' ? item.serviceName : item.visitTypeName}</Text>
                          </Space>
                          {item.itemType === 'service' && item.category && <Text type="secondary">{item.category}</Text>}
                          {item.visitTypeNameEn && <Text type="secondary">{item.visitTypeNameEn}</Text>}
                        </Space>
                      )
                    },
                    {
                      title: 'Normal',
                      dataIndex: 'normalPrice',
                      key: 'normalPrice',
                      render: (value: number) => value != null ? `${value} EGP` : '-'
                    },
                    {
                      title: 'Urgent',
                      dataIndex: 'urgentPrice',
                      key: 'urgentPrice',
                      render: (value: number) => value != null ? `${value} EGP` : '-'
                    },
                    {
                      title: 'Insurance Price',
                      key: 'insurancePrice',
                      render: (_: any, item: InsurancePriceItem) => {
                        const key = `${item.itemType || 'visit_type'}:${item.itemId || item.visitTypeId}`;
                        return (
                        <InputNumber
                          min={0}
                          value={priceValues[key] ?? null}
                          onChange={(value) => setPriceValues((prev) => ({
                            ...prev,
                            [key]: value === null ? null : Number(value),
                          }))}
                          style={{ width: '100%' }}
                          placeholder="No price"
                          addonAfter="EGP"
                        />
                      );
                      }
                    }
                  ]}
                />
                </>
              ) : (
                <Empty description="No price list for this year. Create it first." />
              )}
            </Space>
          </Modal>
        </Content>
      </Layout>
    </Layout>
  );
};

export default InsuranceCompaniesManagement;
